/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  addMocksToSchema,
  createMockStore,
  relayStylePaginationMock,
  IMockStore,
} from '@graphql-tools/mock';
import { SchemaLink } from '@apollo/client/link/schema';
import { buildClientSchema, GraphQLSchema, IntrospectionQuery } from 'graphql';
import introspection from '@slope/graphql/introspection.json' assert { type: 'json' };
import { manyRefs, oneRef } from './helpers';
import { seed as defaultSeed } from './store';

export interface CreateMockLinkOptions {
  schema?: GraphQLSchema;
  store?: IMockStore;
  seed?: (store: IMockStore) => void;
  resolversOverride?: Record<string, any> | ((store: IMockStore) => Record<string, any>);
  preserveResolvers?: boolean;
}

export function createMockLink(opts: CreateMockLinkOptions = {}) {
  const baseSchema =
    opts.schema ??
    buildClientSchema(introspection as unknown as IntrospectionQuery, {
      assumeValid: true,
    });

  const store =
    opts.store ??
    createMockStore({
      schema: baseSchema,
      mocks: {
        Id: () =>
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `id_${Math.random().toString(36).slice(2)}`,
        Timestamp: () => new Date().toISOString(),
        Decimal: () => Number((Math.random() * 500 + 50).toFixed(2)),
      },
    });

  const seed = opts.seed ?? defaultSeed;

  seed(store);

  const defaultResolvers = (s: IMockStore) => ({
    QueryRoot: {
      players: relayStylePaginationMock(s),
      tournaments: relayStylePaginationMock(s),
    },

    Club: {
      players: () => manyRefs(s, 'Player', [2, 6]),
      courses: () => manyRefs(s, 'Course', [1, 3]),
    },

    Course: {
      club: () => oneRef(s, 'Club'),
      holes: () => manyRefs(s, 'Hole', [3, 9]),
    },

    Hole: {
      tees: () => manyRefs(s, 'HoleTee', [2, 3]),
      annotations: () => [],
      // create a green with nested LatLng refs (using only get/set)
      green: () =>
        s.set('HoleGreen', 'AUTO' as any, {
          cap: s.set('LatLng', 'AUTO' as any, { latitude: 40.4, longitude: -3.7 }),
          frontEdge: s.set('LatLng', 'AUTO' as any, { latitude: 40.401, longitude: -3.701 }),
          backEdge: s.set('LatLng', 'AUTO' as any, { latitude: 40.402, longitude: -3.702 }),
        }),
      course: () => oneRef(s, 'Course'),
    },

    HoleTee: {
      coordinates: () => s.set('LatLng', 'AUTO' as any, { latitude: 40.4, longitude: -3.7 }),
    },

    Player: {
      club: () => oneRef(s, 'Club'),
    },

    Tournament: {
      host: () => oneRef(s, 'Club'),
      organizer: () => oneRef(s, 'Club'),
      rounds: () => manyRefs(s, 'Round', [1, 3]),
    },

    Round: {
      course: () => oneRef(s, 'Course'),
      tournament: () => oneRef(s, 'Tournament'),
    },

    Weather: {
      condition: () => 'Partly cloudy',
      humidityPercent: () => 55,
      pressureHpa: () => 1014.2,
      precipitationMm: () => 0.0,
      wind: () =>
        s.set('WeatherWind', 'AUTO' as any, {
          speedMs: 4.5,
          speedKph: 16.2,
          speedMph: 10.1,
          directionDegrees: 225,
          directionCardinal: 'SW',
        }),
      temperature: () =>
        s.set('WeatherTemperature', 'AUTO' as any, { celsius: 22.5, fahrenheit: 72.5 }),
      temperatureRange: () =>
        s.set('WeatherTemperatureRange', 'AUTO' as any, {
          minCelsius: 18.0,
          maxCelsius: 26.0,
          avgCelsius: 22.0,
        }),
    },

    SubscriptionRoot: {
      async *highlight() {
        yield s.set('Highlight', 'AUTO' as any, {
          title: 'Birdie!',
          priority: 1,
          duration: 5,
          repeat: 1,
        }) as any;
      },
    },
  });

  const merged =
    typeof opts.resolversOverride === 'function'
      ? deepMerge(defaultResolvers(store), opts.resolversOverride(store))
      : deepMerge(defaultResolvers(store), opts.resolversOverride ?? {});

  const schemaWithMocks = addMocksToSchema({
    schema: baseSchema,
    store,
    resolvers: () => merged,
    preserveResolvers: opts.preserveResolvers ?? false,
  });

  return new SchemaLink({
    schema: schemaWithMocks,
    context: () => ({ store }),
  });
}

function deepMerge<T extends Record<string, any>>(a: T, b: T): T {
  if (!b) return a;
  const out: any = { ...a };
  for (const k of Object.keys(b)) {
    const av = (a as any)[k];
    const bv = (b as any)[k];
    out[k] = av && typeof av === 'object' && bv && typeof bv === 'object' ? deepMerge(av, bv) : bv;
  }
  return out;
}
